
import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ValidatorFn,
  AbstractControl,
  FormGroup,
  FormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';

@Component({
  selector: 'app-transport-type',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './transport-type.component.html',
  styleUrls: ['./transport-type.component.scss'],
})
export class TransportTypeComponent implements OnInit, OnChanges {
  @Input() label!: string;
  @Input() isSubmitted = false;
  @Input() isRailSelected = false;
  @Input() group: any;
  @Input() index!: number;
  @Output() noAction = new EventEmitter<boolean>();
  @Output() fromObject = new EventEmitter<any>();

  public formGroup: FormGroup;

  // Keep a backup of original validators to restore when rail is NOT selected
  private originalGroupValidators: ValidatorFn[] = [];
  private originalControlValidators = new Map<string, ValidatorFn[]>();

  constructor(private fb: FormBuilder) {
    this.formGroup = this.fb.group(
      {
        origin: [false],
        destination: [false],
        no: [false],
      },
      { validators: [this.atLeastOneCheckedValidator()] }
    );
  }

  ngOnInit(): void {
    // Backup original validators once
    this.originalGroupValidators = [this.atLeastOneCheckedValidator()];
    ['origin', 'destination', 'no'].forEach((name) => {
      const ctl = this.formGroup.get(name);
      const v = ctl?.validator ? [ctl.validator as ValidatorFn] : [];
      this.originalControlValidators.set(name, v);
    });

    if (this.group) {
      this.formGroup.patchValue({
        origin: this.group.value.origin,
        destination: this.group.value.destination,
        no: this.group.value.no,
      });
      this.noAction.emit(false);
      if (this.formGroup.value.no) {
        this.noAction.emit(true);
      }
      this.fromObject.emit(this.formGroup.value);
    }

    // Apply initial bypass (if input arrives before changes hook)
    this.applyBypass(this.isRailSelected);
  }

  ngOnChanges(changes: SimpleChanges) {
    // If either submit or rail flag changes, re-apply bypass logic
    if (changes['isRailSelected'] || changes['isSubmitted']) {
      // Business rule from you: "do not validate if isRailSelected is true"
      this.applyBypass(this.isRailSelected);

      // Optional: if submit is clicked and rail is selected, you can also mark pristine/untouched
      if (this.isSubmitted && this.isRailSelected) {
        this.formGroup.markAsPristine();
        this.formGroup.markAsUntouched();
      }
    }
  }

  /** Apply/remove validators based on rail selection */
  private applyBypass(bypass: boolean) {
    if (bypass) {
      // Remove group-level validator
      this.formGroup.setValidators(null);

      // Remove control-level validators
      ['origin', 'destination', 'no'].forEach((name) => {
        const ctl = this.formGroup.get(name);
        ctl?.clearValidators();
        ctl?.setErrors(null);
        ctl?.updateValueAndValidity({ emitEvent: false });
      });

      // Clear any form-level errors and recompute validity
      this.formGroup.setErrors(null);
      this.formGroup.updateValueAndValidity({ emitEvent: false });
    } else {
      // Restore original group validator
      this.formGroup.setValidators(this.originalGroupValidators);

      // Restore original control validators (if any)
      ['origin', 'destination', 'no'].forEach((name) => {
        const ctl = this.formGroup.get(name);
        const orig = this.originalControlValidators.get(name) ?? [];
        ctl?.setValidators(orig.length ? orig : null);
        ctl?.updateValueAndValidity({ emitEvent: false });
      });

      this.formGroup.updateValueAndValidity({ emitEvent: false });
    }
  }

  /** Custom Validator: require at least one checkbox */
  atLeastOneCheckedValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const group = control as FormGroup;
      return group.get('origin')?.value ||
        group.get('destination')?.value ||
        group.get('no')?.value
        ? null
        : { atLeastOne: true };
    };
  }

  /** You don’t need this if you use the hard bypass above. Keeping it for reference. */
  noCheckCheckedValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const group = control as FormGroup;
      return group.get('origin')?.value ||
        group.get('destination')?.value ||
        group.get('no')?.value
        ? null
        : { atLeastOne: false }; // note: this shape is unusual; typically same key with different boolean isn't used
    };
  }

  onSelect(changed: 'origin' | 'destination' | 'no') {
    const origin = this.formGroup.get('origin');
    const destination = this.formGroup.get('destination');
    const no = this.formGroup.get('no');
    this.noAction.emit(false);
    if (!origin || !destination || !no) return;

    if (changed === 'no' && no.value) {
      origin.setValue(false, { emitEvent: false });
      destination.setValue(false, { emitEvent: false });
      this.noAction.emit(true);
    }

    if (
      (changed === 'origin' || changed === 'destination') &&
      (origin.value || destination.value)
    ) {
      no.setValue(false, { emitEvent: false });
    }

    this.fromObject.emit(this.formGroup.value);
  }
}